import type { EnhancedStore } from '@reduxjs/toolkit';
import type {
    JSX,
    HTMLAttributes,
    ReactElement,
    ComponentType,
    JSXElementConstructor,
} from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { useNavigate, useRoutes } from 'react-router';

import type { TokenScope } from '../../constants';
import {
    EVO_APPS,
    LOGIN_ERROR_ROUTE,
    LOGIN_ORG_ROUTE,
    LOGIN_ROUTE,
    LOGIN_SILENT,
    LOGIN_SUCCESS_ROUTE,
    LOGOUT_ROUTE,
    LOGOUT_SUCCESS_ROUTE,
} from '../../constants';
import { isLoggedInWithBentleyToken } from '../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { getAuthUrl, getBentleyAuthUrl } from '../../util/getAuthUrl';
import { ErrorRedirect } from '../errorRedirect/errorRedirect';
import type { LandingPageProps } from '../landingPage';
import { BentleyLoginExtend } from '../loginExtend/BentleyLoginExtend';
import { BentleyLoginSuccess } from '../loginSuccess/BentleyLoginSuccess';
import { LoginSuccess } from '../loginSuccess/LoginSuccess';
import { BentleyLogout } from '../logout/BentleyLogout';
import { Logout } from '../logout/Logout';
import { CentralOrgSelector } from '../orgSelector/centralOrgSelector/CentralOrgSelector';
import { BentleyOrgHubSelector } from '../orgSelector/orgHubSelector/BentleyOrgHubSelector';
import { OrgHubSelector } from '../orgSelector/orgHubSelector/OrgHubSelector';
import { AccessTokenGuard } from '../routeGuards/AccessTokenGuard';
import { BentleyAccessTokenGuard } from '../routeGuards/BentleyAccessTokenGuard';
import { SkipOrgSelector } from '../skipOrgSelector/SkipOrgSelector';
import type { LoginHandlerContextState, ServiceName } from './LoginContext';
import { DEFAULT_BACKGROUND_IMAGES, LoginContext } from './LoginContext';

export interface LoginHandlerProps {
    guardedRoutes: ReactElement<any, string | JSXElementConstructor<any>> | null;
    LandingPage: ComponentType<LandingPageProps>;
    LandingPageLogo?: ComponentType<HTMLAttributes<SVGElement>>;
    store: EnhancedStore<any>;
    homePagePath?: string;
    includeClientSecret?: boolean;
    service: ServiceName;
    useOrgSelector?: boolean;
    forceOauthPrefix?: boolean;
    createId?: boolean;
    imageList?: string;
    displayComingSoon?: boolean;
    additionalScopes?: TokenScope[];
    bentleyIdEnabled?: boolean;
    evoNextEnabled?: boolean;
}

function renderRoutes(
    isUsingBentley: boolean,
    guardedRoutes: JSX.Element,
    additionalScopes?: TokenScope[],
) {
    return isUsingBentley ? (
        <BentleyAccessTokenGuard additionalScopes={additionalScopes}>
            {guardedRoutes}
        </BentleyAccessTokenGuard>
    ) : (
        <AccessTokenGuard>{guardedRoutes}</AccessTokenGuard>
    );
}

export function LoginHandler({
    guardedRoutes,
    LandingPage,
    LandingPageLogo,
    store,
    homePagePath,
    service,
    includeClientSecret = false,
    imageList = DEFAULT_BACKGROUND_IMAGES,
    useOrgSelector = false,
    forceOauthPrefix = false,
    createId = true,
    displayComingSoon = false,
    additionalScopes = [],
    bentleyIdEnabled = false,
    evoNextEnabled = false,
}: LoginHandlerProps) {
    const isLoggedInWithBentley = isLoggedInWithBentleyToken();
    const navigate = useNavigate();
    // If we run into a LaunchDarkly error and the flag is unset, default to true

    // Note the call on this may reload the page if config is lost
    const authCallback = useCallback(
        () =>
            bentleyIdEnabled
                ? getBentleyAuthUrl({
                      forceOauthPrefix,
                      additionalScopes,
                  })
                : getAuthUrl(forceOauthPrefix, additionalScopes),
        [bentleyIdEnabled, additionalScopes, forceOauthPrefix],
    );
    const [loginContext] = useState<LoginHandlerContextState>({
        imageList,
        includeClientSecret,
        forceOauthPrefix,
        service,
    });

    let orgComponent = <SkipOrgSelector homePagePath={homePagePath} />;
    if (service === 'central') {
        orgComponent = <CentralOrgSelector homePagePath={homePagePath} />;
    } else if (EVO_APPS.includes(service)) {
        orgComponent = (
            <BentleyOrgHubSelector evoNextEnabled={evoNextEnabled} homePagePath={homePagePath} />
        );
    } else if (useOrgSelector) {
        orgComponent = <OrgHubSelector homePagePath={homePagePath} />;
    }

    const routes = useRoutes([
        {
            path: '*',
            element: renderRoutes(bentleyIdEnabled, guardedRoutes as JSX.Element, additionalScopes),
        },
        {
            path: LOGIN_ROUTE,
            element: (
                <LandingPage
                    createId={createId}
                    getSignInUrl={authCallback}
                    Logo={LandingPageLogo}
                    displayComingSoon={displayComingSoon}
                />
            ),
        },
        {
            path: LOGIN_ERROR_ROUTE,
            element: <ErrorRedirect />,
        },
        {
            path: LOGIN_SUCCESS_ROUTE,
            element: bentleyIdEnabled ? <BentleyLoginSuccess /> : <LoginSuccess />,
        },
        {
            path: LOGIN_SILENT,
            element: <BentleyLoginExtend />,
        },
        {
            path: LOGIN_ORG_ROUTE,
            element: orgComponent,
        },
        {
            path: LOGOUT_ROUTE,
            element: isLoggedInWithBentley ? <BentleyLogout /> : <Logout />,
        },
        {
            path: LOGOUT_SUCCESS_ROUTE,
            element: (
                <LandingPage
                    createId={createId}
                    getSignInUrl={authCallback}
                    Logo={LandingPageLogo}
                    displayComingSoon={displayComingSoon}
                />
            ),
        },
    ]);

    // Logs you out if bentleyIdEnabled doesn't match up with what you're signed in with
    useEffect(() => {
        // If we should be logged in with Bentley but have a Seequent access token
        if (bentleyIdEnabled && isLoggedInWithBentley === false) {
            navigate('/logout');
        }
        // If we should be logged in with Seequent but have a Bentley access token
        if (!bentleyIdEnabled && isLoggedInWithBentley === true) {
            navigate('/logout');
        }
    }, [bentleyIdEnabled, isLoggedInWithBentley, navigate]);

    return (
        <Provider store={store}>
            <LoginContext.Provider value={loginContext}>
                {routes as JSX.Element}
            </LoginContext.Provider>
        </Provider>
    );
}
